<template>
  <div class="home">
    <section class="ms-card home-hero">
      <div class="home-hero__avatar">
        <img v-if="userData?.image_url" :src="userData.image_url" alt="" />
        <template v-else>{{ initials }}</template>
      </div>
      <div class="home-hero__main">
        <div class="home-hero__greeting">{{ greeting }}{{ firstName ? `, ${firstName}` : '' }}</div>
        <h1 class="home-hero__name">{{ userData?.fio || 'Пользователь' }}</h1>
        <span v-if="userData?.position_name" class="ms-badge">{{ userData.position_name }}</span>
      </div>
      <div class="home-hero__date">{{ today }}</div>
    </section>

    <div class="home-grid">
      <section class="ms-card home-block">
        <h2 class="home-block__title">Профиль</h2>
        <dl class="home-info">
          <div class="home-info__row">
            <dt><q-icon name="fa-solid fa-building" />Компания</dt>
            <dd>{{ userData?.company_name || '—' }}</dd>
          </div>
          <div class="home-info__row">
            <dt><q-icon name="fa-solid fa-location-dot" />Город</dt>
            <dd>{{ userData?.city_name || '—' }}</dd>
          </div>
          <div class="home-info__row">
            <dt><q-icon name="fa-solid fa-envelope" />Email</dt>
            <dd>
              <a v-if="userData?.email" :href="`mailto:${userData.email}`" class="home-info__link">{{ userData.email }}</a>
              <template v-else>—</template>
            </dd>
          </div>
          <div class="home-info__row">
            <dt><q-icon name="fa-solid fa-id-badge" />Должность</dt>
            <dd>{{ userData?.position_name || '—' }}</dd>
          </div>
        </dl>
      </section>

      <section class="ms-card home-block">
        <h2 class="home-block__title">
          Группы доступа
          <span v-if="groups.length" class="home-block__count">{{ groups.length }}</span>
        </h2>
        <div v-if="groups.length" class="home-groups">
          <span v-for="group in groups" :key="group" class="home-groups__item">{{ group }}</span>
        </div>
        <div v-else class="home-block__empty">Группы не назначены</div>
      </section>
    </div>

    <section class="ms-card home-block">
      <h2 class="home-block__title">
        Руководства пользователя
        <a :href="guideUrl('index')" target="_blank" class="home-guides__all">Открыть все в новой вкладке</a>
      </h2>
      <div class="home-guides">
        <button v-for="guide in guides" :key="guide.slug" type="button" class="home-guides__item" @click="openGuide(guide)">
          <q-icon :name="guide.icon" class="home-guides__icon" />
          <span class="home-guides__text">
            <b>{{ guide.title }}</b>
            <span>{{ guide.hint }}</span>
          </span>
        </button>
      </div>
    </section>

    <q-dialog v-model="guideOpen" maximized>
      <div class="home-guide-view">
        <div class="home-guide-view__bar">
          <b>{{ guide?.title }}</b>
          <a :href="guide && guideUrl(guide.slug)" target="_blank" class="home-guides__all">В новой вкладке</a>
          <q-btn flat round dense icon="close" v-close-popup />
        </div>
        <iframe v-if="guide" :src="guideUrl(guide.slug)" class="home-guide-view__frame" :title="guide.title" />
      </div>
    </q-dialog>
  </div>
</template>

<script>
// HTML-руководства лежат в public/guides (собираются из smremont/docs/myspace_guide)
const GUIDES = [
  {slug: 'intro', title: 'Общие принципы', icon: 'fa-solid fa-compass', hint: 'Меню, списки, фильтры, окна, права'},
  {slug: 'requests', title: 'Заявки и ремонты', icon: 'fa-solid fa-house-chimney', hint: 'Списки по статусам, вкладки карточки'},
  {slug: 'operations', title: 'Операции', icon: 'fa-solid fa-truck', hint: 'Заявки поставщикам, дефектные акты, гарантия'},
  {slug: 'warehouse', title: 'Склад', icon: 'fa-solid fa-warehouse', hint: 'Приходы, отпуск, перемещения, инвентаризация'},
  {slug: 'dicts', title: 'Справочники', icon: 'fa-solid fa-book', hint: 'Тарифы, пакеты работ, сервисы, ЖК'},
  {slug: 'constructor', title: 'Конструктор', icon: 'fa-solid fa-cubes', hint: 'Пресеты, подборы, материалы, метки'},
  {slug: 'reports', title: 'Отчёты', icon: 'fa-solid fa-chart-column', hint: 'Отчёты, генератор отчётов, финансы'},
  {slug: 'admin', title: 'Администрирование', icon: 'fa-solid fa-user-gear', hint: 'Пользователи, роли, права, CRM, CMS'},
  {slug: 'timeline', title: 'Таймлайн', icon: 'fa-solid fa-timeline', hint: 'Грейды, шаги, зоны, мониторинг, журнал'},
  {slug: 'gpr', title: 'Этапы ГПР', icon: 'fa-solid fa-list-check', hint: 'Шаблоны, этапы, ОКК, платежи подрядчикам'},
  {slug: 'work', title: 'Ремонты, Канбан, IT-поддержка', icon: 'fa-solid fa-screwdriver-wrench', hint: 'Быстрый поиск, гарантия, канбан, заявки IT'},
  {slug: 'materials', title: 'Материалы и закупки', icon: 'fa-solid fa-boxes-stacked', hint: 'Материалы, заявки поставщикам, подборы'},
  {slug: 'finance', title: 'Финансы', icon: 'fa-solid fa-coins', hint: 'Платежи, оплаты мастерам, рассрочки, 1С'},
  {slug: 'handbooks', title: 'Справочники My Space', icon: 'fa-solid fa-book-open', hint: 'Справочники раздела «Офис»'},
  {slug: 'reports2', title: 'Отчёты My Space', icon: 'fa-solid fa-chart-pie', hint: 'Отчёты раздела «Офис», генератор'},
  {slug: 'construction', title: 'Черновая, ДДУ, Revit', icon: 'fa-solid fa-building', hint: 'Черновая, ДДУ, Revit, рендеры, проекты'},
  {slug: 'cms', title: 'CMS', icon: 'fa-solid fa-newspaper', hint: 'Контент сайта и приложения'},
  {slug: 'partners', title: 'Кабинет партнёра', icon: 'fa-solid fa-handshake', hint: 'Ремонты, оплаты, учёт, отчёты подрядчика'},
  {slug: 'providers_admin', title: 'Поставщики и администрирование', icon: 'fa-solid fa-truck-field', hint: 'Кабинет поставщика, доступы, сотрудники'},
]

export default {
  name: "HomeView",
  data() {
    return {
      guides: GUIDES,
      guide: null,
      guideOpen: false,
    }
  },
  methods: {
    guideUrl(slug) {
      return `${process.env.BASE_URL}guides/${slug}.html`
    },
    openGuide(guide) {
      this.guide = guide
      this.guideOpen = true
    },
  },
  computed: {
    userData() {
      return this.$store.state.app?.userData
    },
    fioParts() {
      return (this.userData?.fio || '').trim().split(/\s+/).filter(Boolean)
    },
    initials() {
      return this.fioParts.slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?'
    },
    // ФИО хранится как «Фамилия Имя Отчество» — обращаемся по имени
    firstName() {
      return this.fioParts[1] || this.fioParts[0] || ''
    },
    greeting() {
      const hour = new Date().getHours()
      if (hour >= 5 && hour < 12) return 'Доброе утро'
      if (hour >= 12 && hour < 18) return 'Добрый день'
      if (hour >= 18 && hour < 23) return 'Добрый вечер'
      return 'Доброй ночи'
    },
    today() {
      return new Date().toLocaleDateString('ru-RU', {weekday: 'long', day: 'numeric', month: 'long'})
    },
    groups() {
      return (this.userData?.group_names || '').split(',').map(g => g.trim()).filter(Boolean)
    },
  },
};
</script>

<style scoped lang="scss">
.home {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 1100px;
}

.home-hero {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px 24px;
  background: linear-gradient(120deg, var(--ms-surface) 55%, var(--ms-primary-50));
  border: 1px solid var(--ms-border);

  &__avatar {
    width: 64px;
    height: 64px;
    flex-shrink: 0;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--ms-primary), var(--ms-primary-ink));
    color: #fff;
    font-size: 22px;
    font-weight: 700;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__main {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    min-width: 0;
  }

  &__greeting {
    font-size: 13px;
    color: var(--ms-muted);
  }

  &__name {
    margin: 0;
    font-size: 22px;
    line-height: 28px;
    font-weight: 700;
    color: var(--ms-primary-ink);
    overflow-wrap: anywhere;
  }

  &__date {
    margin-left: auto;
    align-self: flex-start;
    font-size: 13px;
    color: var(--ms-text-secondary);
    white-space: nowrap;

    &::first-letter {
      text-transform: uppercase;
    }
  }

  @media (max-width: 576px) {
    flex-wrap: wrap;
    gap: 14px;
    padding: 16px;

    &__avatar {
      width: 52px;
      height: 52px;
      font-size: 18px;
    }

    &__main {
      flex: 1;
    }

    &__name {
      font-size: 18px;
      line-height: 24px;
    }

    &__date {
      order: -1;
      width: 100%;
      margin-left: 0;
    }
  }
}

.home-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.home-block {
  display: flex;
  flex-direction: column;
  padding: 18px 20px;
  border: 1px solid var(--ms-border);

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 12px;
    font-size: 15px;
    line-height: 20px;
    font-weight: 700;
    color: var(--ms-primary-ink);
  }

  &__count {
    padding: 0 8px;
    border-radius: 9999px;
    background: var(--ms-primary-50);
    color: var(--ms-primary);
    font-size: 12px;
    line-height: 20px;
  }

  &__empty {
    font-size: 13px;
    color: var(--ms-muted);
  }

  @media (max-width: 576px) {
    padding: 16px;
  }
}

.home-info {
  margin: 0;

  &__row {
    display: flex;
    gap: 12px;
    padding: 9px 0;
    border-bottom: 1px solid var(--ms-divider);
    font-size: 13px;

    &:last-child {
      border-bottom: none;
    }

    dt {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 120px;
      flex-shrink: 0;
      color: var(--ms-muted);

      .q-icon {
        width: 14px;
        font-size: 12px;
        color: var(--ms-primary);
      }
    }

    dd {
      margin: 0;
      min-width: 0;
      color: var(--ms-text);
      font-weight: 500;
      overflow-wrap: anywhere;
    }
  }

  &__link {
    color: var(--ms-primary);
  }
}

.home-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  &__item {
    padding: 5px 11px;
    border-radius: 9999px;
    border: 1px solid var(--ms-border);
    background: var(--ms-primary-50);
    color: var(--ms-primary-ink);
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
  }
}

.home-guides {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 10px;

  &__all {
    margin-left: auto;
    font-size: 12px;
    font-weight: 500;
    color: var(--ms-primary);
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid var(--ms-border);
    border-radius: 10px;
    background: var(--ms-surface);
    text-align: left;
    cursor: pointer;
    font: inherit;
    transition: border-color .15s, box-shadow .15s;

    &:hover {
      border-color: var(--ms-primary);
      box-shadow: 0 4px 14px rgba(0, 96, 254, .12);
    }
  }

  &__icon {
    margin-top: 2px;
    font-size: 16px;
    color: var(--ms-primary);
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    b {
      font-size: 14px;
      color: var(--ms-primary-ink);
    }

    span {
      font-size: 12px;
      color: var(--ms-muted);
    }
  }
}

.home-guide-view {
  display: flex;
  flex-direction: column;
  background: var(--ms-surface);

  &__bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px 8px 20px;
    border-bottom: 1px solid var(--ms-border);
    color: var(--ms-primary-ink);
  }

  &__frame {
    flex: 1;
    width: 100%;
    border: 0;
  }
}
</style>
